'use client';

import { useEffect, useState } from 'react';
import type { GetValidQuantityProps } from './getValidQuantity/getValidQuantity';
import { getValidQuantity } from './getValidQuantity/getValidQuantity';
import { getValidValues } from './getValidValues/getValidValues';
import { ActionButton } from './ActionButton/ActionButton.client';

const elementsHeight = '2.25rem';

export type QuantitySelectionProps = {
  /**
   * Sets the maximum value the quantity can be.
   * @default 10
   */
  max?: number;
  /**
   * Sets the minimum value the quantity can be.
   * @default 1
   */
  min?: number;
  /**
   * Sets mow much the quantity of the step will be increased/decreased on button click.
   * @default 1
   */
  step?: number;
  /**
   * Sets the initial quantity
   */
  quantity?: number;
  /**
   * callback method which will be triggered when the quantity has changed
   */
  onUpdate?: (quantity: number) => void;
};

/**
 * <p>
 * This component is used to display some quantity. It comes with an increase and decrease button.
 * With the given props, you can adjust the <code>step</code> value with which the quantity will be decreased/increased.
 * You also can set a <code>min</code>/<code>max</code> value for the quantity and define the initial quantity with the <code>quantity</code> prop.
 * </p>
 * */
export const QuantitySelection = ({
  step = 1,
  min = 1,
  max = 10,
  quantity,
  onUpdate,
  ...props
}: QuantitySelectionProps) => {
  const values = getValidValues({
    step,
    min,
    max,
  });
  let initialQuantity = quantity || values.min;
  if (initialQuantity < values.min) {
    initialQuantity = values.min;
  } else if (initialQuantity > values.max) {
    initialQuantity = values.max;
  }

  const [quantityValue, setQuantityValue] = useState(initialQuantity);

  useEffect(() => {
    if (typeof onUpdate === 'function' && quantityValue !== initialQuantity) {
      onUpdate(quantityValue);
    }
  }, [quantityValue, onUpdate, initialQuantity]);

  const handleAction = (type: GetValidQuantityProps['type']) => {
    setQuantityValue((prev) =>
      getValidQuantity({
        currentQuantity: prev,
        step: values.step,
        ...(type === 'increase'
          ? { type: 'increase', max: values.max }
          : { type: 'decrease', min: values.min }),
      }),
    );
  };

  return (
    <div {...props} className="text-size-body1 flex items-center justify-start">
      <ActionButton
        type="Decrease"
        disabled={quantityValue <= values.min}
        onClick={() => {
          handleAction('decrease');
        }}
        height={elementsHeight}
        step={step}
      />
      <div
        style={{
          height: elementsHeight,
          width: '4.5rem',
          lineHeight: elementsHeight,
        }}
        className="border-color-grey-dark mx-1 overflow-hidden rounded-[3px] border text-center select-none"
      >
        {quantityValue}
      </div>
      <ActionButton
        type="Increase"
        disabled={quantityValue >= values.max}
        onClick={() => {
          handleAction('increase');
        }}
        height={elementsHeight}
        step={step}
      />
    </div>
  );
};
